import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useSWRConfig } from 'swr';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { ACCOUNTS_KEY } from '../../accounts/hooks/useAccounts.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { todayInTimeZone } from '../../recurring/data/recurringRepository.js';
import { ExpenseForm } from '../../transactions/components/ExpenseForm.js';
import { TransferForm } from '../../transactions/components/TransferForm.js';
import { useQuickAddOptions } from '../../transactions/hooks/useQuickAdd.js';
import { dayKeyOf, dayLabelOf, timeLabelOf } from '../../transactions/model/timelineGroups.js';
import {
  approvePendingEntry,
  rejectPendingEntry,
  updatePendingEntry,
} from '../data/pendingRepository.js';
import { usePendingEntries } from '../hooks/usePendingEntries.js';
import {
  canQuickApprove,
  effectiveCategory,
  patchFromExpenseDraft,
  patchFromTransferDraft,
  quickApprovePatch,
  titleOf,
  type PendingEntry,
  type PendingKind,
} from '../model/pendingEntry.js';
import styles from './PendingPage.module.css';

/**
 * Chờ duyệt — bank notices waiting for a person (decision AL1, plan `bank-notice-inbox` N2).
 *
 * The end-of-day gesture that replaces typing each transfer into Quick Add: every row already
 * has the amount, the time and who was paid, straight from the bank's email. Three ways out,
 * one row at a time:
 *   - Duyệt: one tap when nothing is missing (a category, or a destination for a transfer);
 *   - Sửa: the ordinary Quick Add form, pre-filled — fix the category, add a note, then
 *     approve now or keep the edit for later;
 *   - Huỷ: the notice never reaches the ledger.
 *
 * Nothing here is on the books until approved, which is why a look-alike already in the
 * ledger (same account, same amount, within a day) is shown: most likely it was typed by
 * hand before the email arrived, and approving would count it twice.
 */
export function PendingPage() {
  const contextState = useCurrentContext();
  const optionsState = useQuickAddOptions();
  const pending = usePendingEntries();
  const { mutate } = useSWRConfig();

  const [editing, setEditing] = useState<{ id: string; kind: PendingKind } | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (contextState.status === 'loading') return <LoadingState label="Đang tải…" />;
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (pending.status === 'loading') return <LoadingState label="Đang tải khoản chờ duyệt…" />;
  if (pending.status === 'error') {
    return <ErrorState message={pending.message} onRetry={pending.retry} />;
  }

  const { context } = contextState;
  const timeZone = context.household.timezone;
  const todayKey = todayInTimeZone(timeZone);
  const { inbox } = pending;
  const refresh = pending.refresh;

  /** An approval posts a real entry, so everything reading the ledger is reread. */
  async function refreshAfterWrite() {
    await Promise.all([
      refresh(),
      mutate(ACCOUNTS_KEY),
      mutate((key) => typeof key === 'string' && key.startsWith('money-flow:')),
    ]);
  }

  function whenOf(instant: string): string {
    return `${dayLabelOf(dayKeyOf(instant, timeZone), todayKey)} ${timeLabelOf(instant, timeZone)}`;
  }

  async function quickApprove(entry: PendingEntry) {
    setBusyId(entry.id);
    setMessage(null);
    try {
      await approvePendingEntry(entry.id, quickApprovePatch(entry));
      setNotice(`Đã ghi "${titleOf(entry)}" ${formatVnd(entry.amount)}.`);
      await refreshAfterWrite();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function reject(entry: PendingEntry, reason: string) {
    setBusyId(entry.id);
    setMessage(null);
    try {
      await rejectPendingEntry(entry.id, reason.trim() === '' ? 'Huỷ trên Chờ duyệt' : reason);
      setRejectingId(null);
      setNotice(`Đã huỷ "${titleOf(entry)}" — không ghi vào sổ.`);
      await refresh();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  const subjects = {
    selfSubjectId: context.selfSubject.id,
    householdSubjectId: context.householdSubject.id,
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Chờ duyệt</h1>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {inbox.count === 0 ? (
        <section className={lists.card}>
          <p className={styles.empty}>Chưa có khoản nào chờ duyệt.</p>
          <p className={lists.rowMeta}>
            Email thông báo giao dịch của ngân hàng sẽ tự hiện ở đây sau vài phút. Khoản nào chưa có
            thì nhập ở <Link to="/them">Thêm giao dịch</Link> như thường.
          </p>
        </section>
      ) : (
        <p className={styles.summary}>
          {inbox.count} khoản · {formatVnd(inbox.total)} · chưa vào sổ
          {inbox.items.length < inbox.count ? ` (hiện ${inbox.items.length} khoản mới nhất)` : ''}
        </p>
      )}

      <ul className={styles.items}>
        {inbox.items.map((entry) => {
          const isBusy = busyId === entry.id;
          const isEditing = editing?.id === entry.id;
          const category = effectiveCategory(entry);
          const initialDate = dayKeyOf(entry.occurredAt, timeZone);

          return (
            <li key={entry.id} className={lists.card} aria-label={titleOf(entry)}>
              <div className={styles.head}>
                <span className={lists.rowText}>
                  <span className={lists.rowName}>{titleOf(entry)}</span>
                  <span className={lists.rowMeta}>
                    {whenOf(entry.occurredAt)} · {entry.account.name}
                    {entry.bank ? ` · Email ${entry.bank}` : ''}
                  </span>
                </span>
                <span className={lists.amount}>{formatVnd(entry.amount)}</span>
              </div>

              <p className={styles.chips}>
                {entry.kind === 'expense' ? (
                  <span
                    className={cx(
                      styles.chip,
                      entry.category === null && category !== null && styles.chipSuggested,
                      category === null && styles.chipMissing,
                    )}
                  >
                    {category === null
                      ? 'Chưa có danh mục'
                      : entry.category === null
                        ? `Gợi ý: ${category.name}`
                        : category.name}
                  </span>
                ) : (
                  <span className={cx(styles.chip, entry.toAccount === null && styles.chipMissing)}>
                    {entry.toAccount === null
                      ? 'Chuyển tiền · chưa chọn tài khoản nhận'
                      : `Chuyển đến ${entry.toAccount.name}`}
                  </span>
                )}
                {entry.forFamily ? <span className={styles.chip}>Chi cho gia đình</span> : null}
              </p>

              {entry.note ? <p className={styles.note}>{entry.note}</p> : null}

              {entry.similar.length > 0 ? (
                <p className={styles.similar} role="note">
                  Có thể đã nhập tay:{' '}
                  {entry.similar
                    .map(
                      (line) =>
                        `${formatVnd(line.amount)} · ${whenOf(line.occurredAt)}${
                          line.description ? ` · ${line.description}` : ''
                        }`,
                    )
                    .join('; ')}
                  . Nếu đúng vậy, hãy Huỷ khoản này.
                </p>
              ) : null}

              {isEditing ? null : (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={cx(lists.rowButton, lists.rowButtonDanger)}
                    disabled={isBusy}
                    onClick={() => {
                      setRejectingId(rejectingId === entry.id ? null : entry.id);
                      setMessage(null);
                    }}
                  >
                    Huỷ
                  </button>
                  <button
                    type="button"
                    className={lists.rowButton}
                    disabled={isBusy}
                    onClick={() => {
                      setEditing({ id: entry.id, kind: entry.kind });
                      setRejectingId(null);
                      setMessage(null);
                    }}
                  >
                    Sửa
                  </button>
                  <button
                    type="button"
                    className={styles.approve}
                    disabled={isBusy || !canQuickApprove(entry)}
                    onClick={() => void quickApprove(entry)}
                  >
                    {isBusy ? 'Đang ghi…' : 'Duyệt'}
                  </button>
                </div>
              )}

              {!isEditing && !canQuickApprove(entry) ? (
                <p className={lists.rowMeta}>
                  {entry.kind === 'expense'
                    ? 'Chọn danh mục qua Sửa rồi duyệt.'
                    : 'Chọn tài khoản nhận qua Sửa rồi duyệt.'}
                </p>
              ) : null}

              {rejectingId === entry.id && !isEditing ? (
                <RejectPrompt
                  disabled={isBusy}
                  onCancel={() => setRejectingId(null)}
                  onConfirm={(reason) => void reject(entry, reason)}
                />
              ) : null}

              {isEditing && editing !== null ? (
                <div className={styles.edit}>
                  <div className={styles.kindToggle} role="radiogroup" aria-label="Loại giao dịch">
                    {(
                      [
                        ['expense', 'Chi tiêu'],
                        ['transfer', 'Chuyển tiền'],
                      ] as const
                    ).map(([kind, label]) => (
                      <button
                        key={kind}
                        type="button"
                        role="radio"
                        aria-checked={editing.kind === kind}
                        className={cx(styles.kind, editing.kind === kind && styles.kindActive)}
                        onClick={() => setEditing({ id: entry.id, kind })}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {optionsState.status !== 'ready' ? (
                    <LoadingState label="Đang tải danh mục và tài khoản…" />
                  ) : editing.kind === 'expense' ? (
                    <ExpenseForm
                      key={`${entry.id}:expense`}
                      options={optionsState.options}
                      subjects={subjects}
                      householdId={context.household.id}
                      timeZone={timeZone}
                      isFamily={context.isFamily}
                      initial={{
                        amount: entry.amount,
                        categoryId: category?.id ?? '',
                        accountId: entry.account.id,
                        date: initialDate,
                        note: entry.note ?? '',
                        forFamily: entry.forFamily,
                      }}
                      review={{
                        primaryLabel: 'Lưu & duyệt',
                        secondaryLabel: 'Lưu, duyệt sau',
                        onSubmit: async (draft, meta) => {
                          const patch = patchFromExpenseDraft(draft, {
                            date: meta.date,
                            initialDate,
                          });
                          if (meta.action === 'primary') {
                            await approvePendingEntry(entry.id, patch);
                            return `Đã ghi "${titleOf(entry)}" ${formatVnd(draft.amount)}.`;
                          }
                          await updatePendingEntry(entry.id, patch);
                          return `Đã lưu — "${titleOf(entry)}" vẫn chờ duyệt.`;
                        },
                      }}
                      onCancel={() => setEditing(null)}
                      onOptionsChanged={() => optionsState.refresh()}
                      onSaved={(saved) => {
                        setEditing(null);
                        setNotice(saved);
                        void refreshAfterWrite();
                      }}
                    />
                  ) : (
                    <TransferForm
                      key={`${entry.id}:transfer`}
                      options={optionsState.options}
                      subjects={subjects}
                      timeZone={timeZone}
                      initial={{
                        amount: entry.amount,
                        fromAccountId: entry.account.id,
                        toAccountId: entry.toAccount?.id ?? '',
                        date: initialDate,
                        note: entry.note ?? '',
                        purpose: entry.purpose ?? '',
                      }}
                      review={{
                        primaryLabel: 'Lưu & duyệt',
                        secondaryLabel: 'Lưu, duyệt sau',
                        onSubmit: async (draft, meta) => {
                          const patch = patchFromTransferDraft(draft, {
                            date: meta.date,
                            initialDate,
                          });
                          if (meta.action === 'primary') {
                            await approvePendingEntry(entry.id, patch);
                            return `Đã ghi chuyển tiền ${formatVnd(draft.amount)}.`;
                          }
                          await updatePendingEntry(entry.id, patch);
                          return `Đã lưu — "${titleOf(entry)}" vẫn chờ duyệt.`;
                        },
                      }}
                      onCancel={() => setEditing(null)}
                      onSaved={(saved) => {
                        setEditing(null);
                        setNotice(saved);
                        void refreshAfterWrite();
                      }}
                    />
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** "Huỷ" asks once more: a rejected notice does not come back when the email is re-read. */
function RejectPrompt({
  disabled,
  onCancel,
  onConfirm,
}: {
  disabled: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const inputId = useId();
  const [reason, setReason] = useState('');

  return (
    <div className={styles.reject}>
      <div className={forms.field}>
        <label htmlFor={inputId}>Lý do (không bắt buộc)</label>
        <input
          id={inputId}
          className={forms.input}
          type="text"
          value={reason}
          autoComplete="off"
          placeholder="Ví dụ: đã nhập tay rồi"
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <div className={styles.rejectActions}>
        <button type="button" className={forms.secondary} disabled={disabled} onClick={onCancel}>
          Không
        </button>
        <button
          type="button"
          className={cx(forms.secondary, styles.danger)}
          disabled={disabled}
          onClick={() => onConfirm(reason)}
        >
          Huỷ khoản này
        </button>
      </div>
    </div>
  );
}
